import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { checkIsOverstay, fetchPassInfo, getLogVehicle, getPassLimitMs, isResidentLog, isTemporaryPassLog, normalizeAccessLog, officerLabel } from "../utils/accessLogs";
import { useRateLimit } from "../utils/rateLimit";
import { confirmDialog } from "../components/ConfirmDialog";
import { accessModeLabel, directionOf, enumKey, methodColorClass, enumOf, methodLabel, normalizeAccessCode, normalizeDependent, subjectTypeLabel } from "../utils/enums";
import * as XLSX from "xlsx";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, InlineModal, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader,
} from "../components/ModalUI";

// ==========================================
// CONSTANTS & STORAGE HELPERS
// ==========================================
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";
const QR_BASE_URL = window.location.origin;
const LOG_PAGE_SIZE = 50;

function safeJsonParse(data) {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function toTitleCase(str) {
  if (!str) return '';
  return String(str).replace(/\w\S*/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}

function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

function setSession(sessionData) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
}

export function getAccessToken() {
  return getSession()?.accessToken || "";
}

export function getRefreshToken() {
  return getSession()?.refreshToken || "";
}

// ==========================================
// HTTP & NETWORK UTILITIES
// ==========================================
async function fetchWithTimeout(url, options = {}, ms = 60000) {
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), ms);

  try {
    const signal = options.signal
      ? (() => {
          const merged = new AbortController();
          options.signal.addEventListener("abort", () => merged.abort());
          ctrl.signal.addEventListener("abort", () => merged.abort());
          return merged.signal;
        })()
      : ctrl.signal;

    return await fetch(url, { ...options, signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out.");
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) throw new Error("No refresh token.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });

  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Refresh failed.");

  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };

  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  let res = await fetchWithTimeout(url, { ...options, headers });

  if (res.status === 401) {
    try {
      const refreshed = await refreshAccessToken();
      token = refreshed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired.");
    }
  }

  return res;
}

// ==========================================
// STRING & FORMATTING HELPERS
// ==========================================
const buildAccessLink = (tenantId, passId, accessCode) =>
  `${QR_BASE_URL}/visitor-pass?tenant=${encodeURIComponent(tenantId)}&pass=${encodeURIComponent(passId)}&code=${encodeURIComponent(accessCode)}`;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getDuration(start, end) {
  if (!start || !end) return "—";
  const diff = new Date(end) - new Date(start);
  if (diff < 0) return "—";
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) return `${hours} hr${hours > 1 ? "s" : ""} ${mins} min${mins !== 1 ? "s" : ""}`;
  return `${mins} min${mins !== 1 ? "s" : ""}`;
}

const maskMethod = methodLabel;
const getMethodColorClass = methodColorClass;
const maskType = subjectTypeLabel;

function getHostInfo(log) {
  const phase = log.phaseName || "";
  const num = log.buildingNumber || log.houseNumber || "";
  const isPoHost = enumKey(log.hostType || log.accessCodeCreatedByType) === "primaryoccupant";
  const poId = log.primaryOccupantId || log.hostId;
  let link = null;
  if (isPoHost && log.tenantId && poId) link = `/tenants/${log.tenantId}/occupants/${poId}`;
  else if (log.tenantId || log.hostId) link = `/tenants/${log.tenantId || log.hostId}`;
  return {
    link,
    name: toTitleCase(log.hostName || log.accessCodeCreatedByName || log.tenantFullName || log.tenantName || "Host resident"),
    type: log.hostType || log.accessCodeCreatedByType || "Tenant",
    phone: log.hostPhoneNumber || log.hostPhone || log.tenantPhone || null,
    imageUrl: log.hostImageUrl || log.tenantImageUrl || null,
    building: phase && num ? `${phase} - ${num}` : (num || log.hostBuilding || log.tenantBuilding || phase || "—"),
  };
}

// ==========================================
// SUBCOMPONENTS
// ==========================================
const VisitProgressBar = ({ checkIn, checkOut, isActive, subjectType, limitAt = null }) => {
  const [elapsed, setElapsed] = useState("");
  const [progressPct, setProgressPct] = useState(0);
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    if (!checkIn) return;
    const start = new Date(checkIn).getTime();

    if (!isActive && checkOut) {
      setElapsed(getDuration(checkIn, checkOut));
      setProgressPct(100);
      return;
    }

    const update = () => {
      const now = new Date().getTime();
      const diff = Math.max(0, now - start);
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);

      let timeStr = "";
      if (hours > 0) timeStr += `${hours} hr${hours > 1 ? "s" : ""} `;
      timeStr += `${mins} min${mins !== 1 ? "s" : ""}`;
      setElapsed(timeStr.trim() || "Just now");

      const allowed = limitAt ? Math.max(1, limitAt - start) : 12 * 3600000;
      const pct = (diff / allowed) * 100;
      setProgressPct(Math.min(100, Math.max(5, pct)));

      setIsOverdue(Boolean(isActive && limitAt && now > limitAt));
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [checkIn, checkOut, isActive, subjectType, limitAt]);

  if (!checkIn) return null;

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="fs-7 fw-bold text-gray-600">
          {isActive ? "Duration Tracker" : "Total Duration"}
        </span>
        <span className={`fs-5 fw-bold ${isActive ? (isOverdue ?"text-danger" : "text-success") : "text-success"}`}>
          {elapsed}
        </span>
      </div>
      <div className="progress h-20px bg-gray-200 w-100 rounded-2 overflow-hidden">
        <div
          className={`progress-bar rounded-2 ${
 isActive
 ? isOverdue
 ?"bg-danger progress-bar-striped progress-bar-animated"
                : "bg-success progress-bar-striped progress-bar-animated"
              : "bg-success"
          }`}
          role="progressbar"
          style={{ width: `${isActive ? 100 : progressPct}%`, transition: "width 1s ease" }}
          aria-valuenow={isActive ? 100 : progressPct}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      {isActive && (
        <div className="d-flex align-items-center justify-content-end gap-2 mt-2">
          {isOverdue ? (
            <>
              <i className="ki-outline ki-notification-status text-danger fs-5 animate-pulse" />
              <span className="fs-7 text-danger fw-bold">Pass time limit exceeded</span>
            </>
          ) : (
            <>
              <span className="spinner-grow spinner-grow-sm text-success opacity-75" style={{ width: "0.75rem", height: "0.75rem" }} />
              <span className="fs-7 text-success fw-bold">{maskType(subjectType)} currently inside</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StatCardSkeleton = () => (
  <div className="col-12 col-md-6 col-xl-3">
    <div className="p-6 h-100 border-end border-white border-opacity-10 placeholder-glow">
      <div className="d-flex align-items-center">
        <div className="symbol symbol-50px me-4">
          <div className="placeholder rounded w-50px h-50px bg-white bg-opacity-15" />
        </div>
        <div className="flex-grow-1">
          <div className="placeholder col-6 mb-2 rounded bg-white bg-opacity-25 h-15px" />
          <div className="placeholder col-4 mb-2 rounded d-block bg-white bg-opacity-35 h-30px" />
          <div className="placeholder col-8 rounded bg-white bg-opacity-15 h-12px" />
        </div>
      </div>
    </div>
  </div>
);

const ActivityLogSkeleton = () => (
  <div className="d-flex flex-column gap-3">
    {[1, 2, 3, 4, 5].map((n) => (
      <div
        key={n}
        className="d-flex align-items-center mb-3 py-4 px-6 rounded-3 border placeholder-glow bg-light bg-opacity-40 position-relative overflow-hidden"
        style={{ borderColor: "#e2e8f0", minHeight: "90px" }}
      >
        <div className="position-absolute top-0 start-0 h-100 bg-secondary bg-opacity-25 w-4px" />
        <div className="d-flex align-items-center me-4" style={{ width: "460px", flexShrink: 0 }}>
          <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
            <div className="placeholder rounded-circle w-45px h-45px bg-secondary bg-opacity-20" />
          </div>
          <div className="d-flex flex-column overflow-hidden flex-grow-1 gap-2">
            <div className="placeholder col-7 rounded h-18px bg-secondary bg-opacity-20" />
            <div className="placeholder col-5 rounded h-14px bg-secondary bg-opacity-15" />
          </div>
        </div>
        <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
          <div className="d-none d-sm-block text-center ms-4">
            <div className="placeholder col-12 rounded h-12px mb-1 w-50px bg-secondary bg-opacity-15" />
            <div className="placeholder col-12 rounded h-15px w-70px bg-secondary bg-opacity-20" />
          </div>
          <div className="d-none d-sm-block text-center ms-4">
            <div className="placeholder col-12 rounded h-12px mb-1 w-40px bg-secondary bg-opacity-15" />
            <div className="placeholder col-12 rounded h-15px w-70px bg-secondary bg-opacity-20" />
          </div>
          <div className="text-center ms-4">
            <div className="placeholder col-12 rounded h-12px mb-1 w-50px bg-secondary bg-opacity-15" />
            <div className="placeholder col-12 rounded h-15px w-70px bg-secondary bg-opacity-20" />
          </div>
          <div className="placeholder rounded-circle w-30px h-30px ms-2 bg-secondary bg-opacity-15" />
        </div>
      </div>
    ))}
  </div>
);

function GateActionModal({ modalRef, actionType, estateId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", accessPoint: "Main Gate" });
  const isCheckIn = actionType === "in";

  const resetAndClose = () => {
    setForm({ code: "", accessPoint: "Main Gate" });
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    onClose?.();
  };

  const waitSeconds = useRateLimit(`POST /estates/:id/access-logs/${isCheckIn ? "check-in" : "check-out"}`);

  const handleSubmit = async () => {
    if (waitSeconds > 0) return;
    if (!estateId) return toast.error("Estate ID is missing.");
    if (!form.code.trim()) return toast.error("Please enter the access code.");
    if (isCheckIn && !form.accessPoint.trim()) return toast.error("Access point is required.");

    setLoading(true);
    try {
      const endpoint = isCheckIn ? "check-in" : "check-out";
      const url = `${API_BASE}/estates/${estateId}/access-logs/${endpoint}`;

      const bodyData = isCheckIn
        ? {
            code: form.code.trim(),
            verificationMethod: 0,
            accessPoint: form.accessPoint.trim(),
          }
        : {
            code: form.code.trim(),
          };

      const res = await authFetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });

      if (!res.ok) {
        const rawText = await res.text();
        const errObj = safeJsonParse(rawText);

        if (res.status === 404) {
          throw new Error(errObj?.message || "404 Not Found: Check if the Estate ID or Access Code exists.");
        }
        throw new Error(errObj?.message || errObj?.title || `Failed to check ${actionType}`);
      }

      toast.success(`Successfully checked ${isCheckIn ? "in" : "out"}!`);
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon={isCheckIn ? "ki-entrance-left" : "ki-entrance-right"}
      title={isCheckIn ? "Manual Check-In" : "Manual Check-Out"}
      onClose={resetAndClose}
      closeDisabled={loading}
      width={520}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn btn-${isCheckIn ? "success" : "danger"} d6-mbtn`}
            onClick={handleSubmit}
            disabled={loading || waitSeconds > 0}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : waitSeconds > 0 ? (
              `Try again in ${waitSeconds}s`
            ) : isCheckIn ? (
              "Check In Visitor"
            ) : (
              "Check Out Visitor"
            )}
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-4">
        <Field label="Access Code" required>
          <input
            type="text"
            className="d6m-input fw-bold text-center fs-3"
            style={{ minHeight: 54 }}
            placeholder="e.g. ABC123"
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
        </Field>
        {isCheckIn && (
          <Field label="Access Point" required>
            <input
              type="text"
              className="d6m-input"
              value={form.accessPoint}
              onChange={(e) => setForm((p) => ({ ...p, accessPoint: e.target.value }))}
            />
          </Field>
        )}
      </div>
    </ModalShell>
  );
}

function DownloadAccessLogModal({ modalRef, rawAccessLogs = [] }) {
  const todayStr = new Date().toISOString().split("T")[0];
  const [personnelFilter, setPersonnelFilter] = useState("");
  const [subjectTypeFilter, setSubjectTypeFilter] = useState("all");
  const [visitStatusFilter, setVisitStatusFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("today");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [search, setSearch] = useState("");

  const hideModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const handlePresetChange = (e) => {
    const preset = e.target.value;
    setDatePreset(preset);

    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else {
      const today = new Date();
      const endStr = today.toISOString().split("T")[0];
      setEndDate(endStr);

      if (preset === "today") {
        setStartDate(endStr);
      } else if (preset === "last7") {
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);
        setStartDate(last7.toISOString().split("T")[0]);
      } else if (preset === "last30") {
        const last30 = new Date();
        last30.setDate(today.getDate() - 30);
        setStartDate(last30.toISOString().split("T")[0]);
      }
    }
  };

  const uniquePersonnel = useMemo(() => {
    const set = new Set();
    (rawAccessLogs || []).forEach((l) => {
      if (l.checkedInByName && (!l.checkedInByType || l.checkedInByType !== "Tenant")) {
        set.add(l.checkedInByName);
      }
      if (l.checkedOutByName && (!l.checkedOutByType || l.checkedOutByType !== "Tenant")) {
        set.add(l.checkedOutByName);
      }
    });
    return Array.from(set).sort();
  }, [rawAccessLogs]);

  const filteredLogs = useMemo(() => {
    return (rawAccessLogs || []).filter((l) => {
      const q = search.trim().toLowerCase();
      const sName = (l.subjectName || l.tenantName || l.visitorName || "").toLowerCase();
      const sCode = (l.code || l.accessCode || "").toLowerCase();
      const sHouse = (l.buildingNumber || l.houseNumber || "").toLowerCase();
      const sPhase = (l.phaseName || "").toLowerCase();

      const matchSearch = !q || sName.includes(q) || sCode.includes(q) || sHouse.includes(q) || sPhase.includes(q);
      const matchType = subjectTypeFilter === "all" || l.subjectType === subjectTypeFilter;
      const matchStatus =
        visitStatusFilter === "all" ||
        (visitStatusFilter === "inside" && l.isActiveVisit) ||
        (visitStatusFilter === "checkedout" && !l.isActiveVisit && l.checkedOutAt);

      const matchPersonnel =
        !personnelFilter ||
        l.checkedInByName === personnelFilter ||
        l.checkedOutByName === personnelFilter;

      let matchDate = true;
      if (startDate || endDate) {
        const sTime = startDate ? new Date(startDate).getTime() : 0;
        const eDate = endDate ? new Date(endDate) : new Date("2099-12-31");
        eDate.setHours(23, 59, 59, 999);
        const eTime = eDate.getTime();

        const inTime = l.checkedInAt ? new Date(l.checkedInAt).getTime() : null;
        const outTime = l.checkedOutAt ? new Date(l.checkedOutAt).getTime() : null;

        const inInRange = inTime && inTime >= sTime && inTime <= eTime;
        const outInRange = outTime && outTime >= sTime && outTime <= eTime;

        matchDate = Boolean(inInRange || outInRange);
      }

      return matchSearch && matchType && matchStatus && matchPersonnel && matchDate;
    });
  }, [rawAccessLogs, search, subjectTypeFilter, visitStatusFilter, personnelFilter, startDate, endDate]);

  const handleExportXLSX = () => {
    if (filteredLogs.length === 0) {
      toast.error("No access log records match your selected filters.");
      return;
    }

    const data = filteredLogs.map((l, index) => ({
      "S/N": index + 1,
      "Subject Name": toTitleCase(l.subjectName || l.tenantName || l.visitorName || "—"),
      "Subject Type": maskType(l.subjectType),
      "House / Phase": `${l.phaseName ? l.phaseName + " - " : ""}${l.buildingNumber || l.houseNumber || "—"}`,
      "Access Code": l.code || l.accessCode || "—",
      "Transport Mode": (() => { const v = getLogVehicle(l); return v ? `Vehicle (${v.plate || (v.isUnlisted ? "Unlisted" : "No Plate")})` : "Pedestrian"; })(),
      "Check-in Time": l.checkedInAt ? new Date(l.checkedInAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "—",
      "Check-out Time": l.checkedOutAt ? new Date(l.checkedOutAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "—",
      "Verified / Checked-in By": toTitleCase(l.checkedInByName || l.accessCodeCreatedByName || "—"),
      "Checked-out By": toTitleCase(l.checkedOutByName || "—"),
      Method: maskMethod(l.verificationMethod),
      Status: l.isActiveVisit ? "Inside" : l.checkedOutAt ? "Checked Out" : "Completed",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Access Logs");
    XLSX.writeFile(workbook, `Estate_Access_Logs_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Excel log downloaded successfully!");
    hideModal();
  };

  const handleExportPDF = () => {
    if (filteredLogs.length === 0) {
      toast.error("No access log records match your selected filters.");
      return;
    }

    const printWin = window.open("", "_blank");
    if (!printWin) {
      toast.error("Please allow popups to generate PDF.");
      return;
    }

    const rowsHtml = filteredLogs
      .map((l, idx) => {
        const houseText = `${l.phaseName ? l.phaseName + " - " : ""}${l.buildingNumber || l.houseNumber || "—"}`;
        const statusText = l.isActiveVisit ? "Inside" : l.checkedOutAt ? "Checked Out" : "Completed";
        const statusBadgeClass = l.isActiveVisit ? "badge-warning" : l.checkedOutAt ? "badge-info" : "badge-success";
        const verifiedBy = toTitleCase(l.checkedInByName || l.accessCodeCreatedByName || "—");
        const checkInStr = l.checkedInAt
          ? new Date(l.checkedInAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
          : "—";
        const checkOutStr = l.checkedOutAt
          ? new Date(l.checkedOutAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
          : "—";
        const printVeh = getLogVehicle(l);
        const transport = printVeh ? `Vehicle (${printVeh.plate || (printVeh.isUnlisted ? "Unlisted" : "Car")})` : "Pedestrian";

        return `
          <tr>
            <td style="text-align: center;">${idx + 1}</td>
            <td><strong>${toTitleCase(l.subjectName || l.tenantName || l.visitorName || "—")}</strong></td>
            <td>${maskType(l.subjectType)}</td>
            <td>${houseText}</td>
            <td style="font-family: monospace; font-weight: bold;">${l.code || l.accessCode || "—"}</td>
            <td>${transport}</td>
            <td>${checkInStr}</td>
            <td>${checkOutStr}</td>
            <td>${verifiedBy}</td>
            <td><span class="badge ${statusBadgeClass}">${statusText}</span></td>
          </tr>
        `;
      })
      .join("");

    const logoUrl = window.location.origin + "/assets/media/zangon-logo-white.svg";

    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Estate Access Logs Report</title>
        <style>
          @page { size: A4 landscape; margin: 10mm; }
          body {
            font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            color: #1F2937; margin: 0; padding: 0; background: #FFFFFF;
            -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
          }
          .pdf-header {
            background: linear-gradient(135deg, #065F46 0%, #0F9D58 100%);
            padding: 24px 20px; text-align: center; border-radius: 8px; margin-bottom: 16px; color: #FFFFFF;
          }
          .pdf-header img { max-height: 48px; width: auto; margin: 0 auto 12px auto; display: block; }
          .pdf-title { font-size: 24px; font-weight: 800; text-transform: uppercase; margin: 0 0 4px 0; color: #FFFFFF; }
          .pdf-subtitle { font-size: 14px; opacity: 0.9; color: #E6F4EA; }
          .meta-box {
            background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 14px 18px;
            margin-bottom: 16px; font-size: 14px; display: flex; justify-content: space-between; align-items: center; color: #334155;
          }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th {
            background: #065F46; color: #FFFFFF; font-weight: 700; text-align: left;
            padding: 10px 12px; border: 1px solid #065F46; text-transform: uppercase; font-size: 12px;
          }
          td { padding: 9px 12px; border-bottom: 1px solid #E2E8F0; vertical-align: middle; }
          tr:nth-child(even) { background: #F8FAFC; }
          .badge { display: inline-block; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; }
          .badge-success { background: #D1FAE5; color: #065F46; }
          .badge-warning { background: #FEF3C7; color: #92400E; }
          .badge-info { background: #E0F2FE; color: #075985; }
          .pdf-footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #E2E8F0; text-align: center; font-size: 12px; color: #64748B; }
        </style>
      </head>
      <body>
        <div class="pdf-header">
          <img src="${logoUrl}" alt="Zangon Logo" />
          <div class="pdf-title">Estate Access Logs Report</div>
          <div class="pdf-subtitle">Official Access Control Ledger</div>
        </div>
        <div class="meta-box">
          <div><strong>Generated On:</strong> ${new Date().toLocaleString("en-GB")}</div>
          <div><strong>Personnel Filter:</strong> ${personnelFilter || "All Personnel"}</div>
          <div><strong>Total Logs:</strong> ${filteredLogs.length}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">#</th>
              <th>Subject Name</th>
              <th>Type</th>
              <th>House / Phase</th>
              <th>Access Code</th>
              <th>Transport</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Verified By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
        <div class="pdf-footer">
          Zangon Estate Access Management • Confidential System Report
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
          };
        </script>
      </body>
      </html>
    `;

    printWin.document.open();
    printWin.document.write(pdfContent);
    printWin.document.close();
    toast.success("PDF report generated successfully!");
    hideModal();
  };

  const resetFilters = () => {
    const resetDateStr = new Date().toISOString().split("T")[0];
    setPersonnelFilter("");
    setSubjectTypeFilter("all");
    setVisitStatusFilter("all");
    setDatePreset("today");
    setStartDate(resetDateStr);
    setEndDate(resetDateStr);
    setSearch("");
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Access Logs"
      onClose={hideModal}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={hideModal}>Cancel</button>
          <button type="button" className="btn btn-light-primary d6-mbtn" onClick={handleExportPDF}>
            <i className="ki-outline ki-file-down fs-5 me-2.5" />
            Export PDF
          </button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleExportXLSX}>
            <i className="ki-outline ki-file-sheet fs-5 me-2.5 text-white" />
            Export Excel
          </button>
        </>
      }
    >
      <div className="d6m-section-head"><span>Filter your export</span></div>

      <div className="d-flex flex-column gap-4">
        <Field label="Personnel / Security Officer">
          <select className="d6m-input" value={personnelFilter} onChange={(e) => setPersonnelFilter(e.target.value)}>
            <option value="">All Personnel</option>
            {uniquePersonnel.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field label="Subject Type">
          <select className="d6m-input" value={subjectTypeFilter} onChange={(e) => setSubjectTypeFilter(e.target.value)}>
            <option value="all">All Types (Visitors & Tenants)</option>
            <option value="Visitor">Visitor</option>
            <option value="Tenant">Tenant</option>
            <option value="Dependent">Dependent</option>
          </select>
        </Field>

        <div className="row g-4">
          <Field className="col-6" label="Date Range">
            <select className="d6m-input" value={datePreset} onChange={handlePresetChange}>
              <option value="today">Today</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </Field>
          <Field className="col-6" label="Visit Status">
            <select className="d6m-input" value={visitStatusFilter} onChange={(e) => setVisitStatusFilter(e.target.value)}>
              <option value="all">All Visits</option>
              <option value="inside">Currently Inside Estate</option>
              <option value="checkedout">Checked Out / Completed</option>
            </select>
          </Field>
        </div>

        <Field label="Search Keyword">
          <input
            type="text"
            className="d6m-input"
            placeholder="Search name, code, house..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
      </div>

      <div className="d6m-note">
        <i className="ki-outline ki-information-5"></i>
        <span className="flex-grow-1">
          Ready to export <strong className="text-gray-900">{filteredLogs.length}</strong> matching log{filteredLogs.length !== 1 ? "s" : ""}.
        </span>
        <button type="button" className="d6m-link-btn" onClick={resetFilters}>Reset filters</button>
      </div>
    </ModalShell>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function EstateAccess() {
  const params = useParams();
  const session = getSession();
  const estateId = session?.estateId || "";
  const tenantId = params?.id || "estate";

  const [pageReady, setPageReady] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [rawAccessLogs, setRawAccessLogs] = useState([]);
  const [insidePeople, setInsidePeople] = useState([]);
  const [passInfo, setPassInfo] = useState({});
  const [todayLogs, setTodayLogs] = useState([]);
  // Server-side counts from GET /access-logs/summary ({ today, thisWeek, thisMonth }); estate-wide only.
  const [accessSummary, setAccessSummary] = useState(null);
  const [checkingPasses, setCheckingPasses] = useState(false);
  const [isOverstayModalOpen, setIsOverstayModalOpen] = useState(false);
  const [overstayTick, setOverstayTick] = useState(0);
  const [, setTenantState] = useState({ accessCards: [] });
  const [, setOwnersList] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [modalImgError, setModalImgError] = useState(false);

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(false);

  const [, setInsidePage] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [logTotalCount, setLogTotalCount] = useState(0);

  const todayStr = new Date().toISOString().split("T")[0];

  const [logFilters, setLogFilters] = useState({
    search: "",
    isActiveVisit: "",
    verificationMethod: "",
    vehicle: "",
    accessPoint: "",
    startDate: todayStr,
    endDate: todayStr,
    sortBy: "",
    datePreset: "today",
  });

  const abortControllerRef = useRef(null);
  const pageCacheRef = useRef({});
  const currentFiltersKeyRef = useRef("");

  const checkInModalRef = useRef(null);
  const checkOutModalRef = useRef(null);
  const downloadLogModalRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Metronic theme initialization
  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };

    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  // Modal overflow behavior
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    } else {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const loadPassInfo = useCallback(async (logs) => {
    setCheckingPasses(true);
    try {
      const found = await fetchPassInfo({ apiBase: API_BASE, estateId, logs, authFetch, includeCheckedOut: true });
      if (Object.keys(found).length) setPassInfo((prev) => ({ ...prev, ...found }));
    } finally {
      setCheckingPasses(false);
    }
  }, [estateId]);

  // Reads every page of access logs matching `params` (stats need all rows, not one page).
  const fetchAllAccessLogs = useCallback(async (params) => {
    const PAGE = 200;
    const all = [];
    for (let page = 1; page <= 25; page++) {
      const qp = new URLSearchParams({ ...params, pageIndex: String(page), pageSize: String(PAGE) });
      if (tenantId && tenantId !== "estate") qp.append("tenantId", tenantId);
      const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?${qp.toString()}`);
      if (!res.ok) throw new Error(`Failed to load access logs (${res.status})`);
      const json = await res.json();
      const items = Array.isArray(json?.data?.data) ? json.data.data.map(normalizeAccessLog) : [];
      all.push(...items);
      const total = json?.data?.totalCount ?? 0;
      if (items.length < PAGE || all.length >= total) break;
    }
    return all;
  }, [estateId, tenantId]);

  const fetchAccessSummary = useCallback(async () => {
    if (!estateId || (tenantId && tenantId !== "estate")) {
      setAccessSummary(null);
      return;
    }
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs/summary`);
      if (!res.ok) return;
      const json = safeJsonParse(await res.text());
      setAccessSummary(json?.data || null);
    } catch (e) {
      console.error("Failed to fetch access summary:", e);
    }
  }, [estateId, tenantId]);

  const fetchActiveVisits = useCallback(async () => {
    if (!estateId) return;
    fetchAccessSummary();
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const [activeLogs, todays] = await Promise.all([
        fetchAllAccessLogs({ isActiveVisit: "true" }),
        fetchAllAccessLogs({ startDate: startOfToday.toISOString() }),
      ]);
      setTodayLogs(todays);
      // Overstays are flagged by the API, so only today's entries need their pass looked up
      // (to split residents from visitors); other passes load when opened.
      loadPassInfo(todays);
      setInsidePeople(
        activeLogs.map((l) => ({
          ...l,
          id: l.id,
          name: toTitleCase(l.dependentName || l.subjectName || l.visitorName || "Visitor"),
          role: l.accessPoint || "Guest",
          code: l.code,
          since: l.checkedInAt
            ? new Date(l.checkedInAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
            : "—",
          accessPoint: l.accessPoint,
        }))
      );
    } catch (e) {
      console.error("Failed to fetch active visits:", e);
    }
  }, [estateId, fetchAllAccessLogs, loadPassInfo, fetchAccessSummary]);

  // Re-evaluate overstays every minute while the page is open.
  useEffect(() => {
    const t = setInterval(() => setOverstayTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const overstayers = useMemo(
    () => insidePeople
      .filter((l) => checkIsOverstay(l, passInfo))
      .map((l) => ({ ...l, limitAt: getPassLimitMs(l, passInfo), host: getHostInfo(l) }))
      // Longest overstay first; rows whose pass expiry isn't loaded yet sort by check-in time.
      .sort((a, b) => (a.limitAt ?? new Date(a.checkedInAt).getTime()) - (b.limitAt ?? new Date(b.checkedInAt).getTime())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insidePeople, passInfo, overstayTick]
  );

  const buildFiltersKey = useCallback((filters) => {
    return JSON.stringify({
      search: filters.search,
      isActiveVisit: filters.isActiveVisit,
      verificationMethod: filters.verificationMethod,
      accessPoint: filters.accessPoint,
      startDate: filters.startDate,
      endDate: filters.endDate,
      sortBy: filters.sortBy,
      datePreset: filters.datePreset,
    });
  }, []);

  const fetchAccessLogs = useCallback(
    async (page, filters, forceRefresh = false) => {
      if (!estateId) return;

      const filtersKey = buildFiltersKey(filters);
      if (filtersKey !== currentFiltersKeyRef.current) {
        pageCacheRef.current = {};
        currentFiltersKeyRef.current = filtersKey;
      }

      if (!forceRefresh && pageCacheRef.current[page]) {
        const cached = pageCacheRef.current[page];
        setRawAccessLogs(cached.logs);
        setActivityData(cached.activityEvents);
        setLogTotalCount(cached.totalCount);
        return;
      }

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLogLoading(true);
      try {
        const qp = new URLSearchParams();
        qp.append("pageIndex", page.toString());
        qp.append("pageSize", LOG_PAGE_SIZE.toString());
        if (tenantId && tenantId !== "estate") qp.append("tenantId", tenantId);
        if (filters.search) qp.append("search", filters.search);
        if (filters.isActiveVisit === "true" || filters.isActiveVisit === "false") {
          qp.append("isActiveVisit", filters.isActiveVisit);
        }
        if (filters.verificationMethod !== "") qp.append("verificationMethod", filters.verificationMethod);
        if (filters.accessPoint) qp.append("accessPoint", filters.accessPoint);
        if (filters.startDate) qp.append("startDate", new Date(filters.startDate).toISOString());
        if (filters.endDate) {
          const eDate = new Date(filters.endDate);
          eDate.setHours(23, 59, 59, 999);
          qp.append("endDate", eDate.toISOString());
        }
        if (filters.sortBy) qp.append("sortBy", filters.sortBy);

        const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?${qp.toString()}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          const errObj = safeJsonParse(await res.text());
          throw new Error(errObj?.message || errObj?.title || `Failed to fetch access logs (Status: ${res.status})`);
        }

        const json = await res.json();
        let logs = Array.isArray(json?.data?.data) ? json.data.data.map(normalizeAccessLog) : [];
        let totalCount = json?.data?.totalCount || 0;

        const events = [];
        logs.forEach((l) => {
          const name = toTitleCase(l.subjectName || l.dependentName || l.visitorName || "Visitor");
          const role = l.verificationMethod || l.accessPoint || "Guest";
          if (l.checkedInAt) {
            events.push({
              ...l,
              id: `${l.id}_in`,
              baseId: l.id,
              date: l.checkedInAt.split("T")[0],
              name,
              role,
              direction: "IN",
              time: new Date(l.checkedInAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
              status: l.isActiveVisit || (l.checkedInAt && !l.checkedOutAt) ? "Inside" : "Outside",
              rawTime: new Date(l.checkedInAt).getTime(),
            });
          }
          if (l.checkedOutAt) {
            events.push({
              ...l,
              id: `${l.id}_out`,
              baseId: l.id,
              date: l.checkedOutAt.split("T")[0],
              name,
              role,
              direction: "OUT",
              time: new Date(l.checkedOutAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
              status: "Outside",
              rawTime: new Date(l.checkedOutAt).getTime(),
            });
          }
        });
        events.sort((a, b) => {
          const aOverstay = checkIsOverstay(a);
          const bOverstay = checkIsOverstay(b);
          if (aOverstay && !bOverstay) return -1;
          if (!aOverstay && bOverstay) return 1;
          return b.rawTime - a.rawTime;
        });

        pageCacheRef.current[page] = { logs, activityEvents: events, totalCount };
        setRawAccessLogs(logs);
        setActivityData(events);
        setLogTotalCount(totalCount);
      } catch (err) {
        if (err.name === "AbortError" || err.message === "Request timed out.") return;
        toast.error(err.message || "Failed to load access logs.");
      } finally {
        setLogLoading(false);
      }
    },
    [estateId, tenantId, buildFiltersKey]
  );

  const fetchSideData = useCallback(async () => {
    if (!estateId) {
      setIsDataLoading(false);
      return;
    }
    setIsDataLoading(true);
    try {
      const [primaryOccupantsRes, tenantsRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/primary-occupants?pageIndex=0&pageSize=30`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=30`),
      ]);

      const combined = [];

      if (primaryOccupantsRes.ok) {
        const json = await primaryOccupantsRes.json();
        const items = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
        items.forEach((o) => combined.push({ ...o, _type: "primary-occupant" }));
      }

      if (tenantsRes.ok) {
        const json = await tenantsRes.json();
        const items = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
        items.forEach((t) => combined.push({ ...normalizeDependent(t), id: t.tenantId || t.id, _type: "tenant" }));
      }

      setOwnersList(combined);

      let activeCards = [];
      if (tenantId !== "estate") {
        const passRes = await authFetch(
          `${API_BASE}/estates/${estateId}/tenants/${tenantId}/access-codes/active?pageIndex=1&pageSize=100`
        );
        const passJson = await passRes.json();
        const codes = Array.isArray(passJson?.data?.data)
          ? passJson.data.data
          : Array.isArray(passJson?.data)
          ? passJson.data
          : [];
        activeCards = codes.map(normalizeAccessCode).map((c) => ({
          id: c.id,
          tenantId: c.tenantId,
          dependentId: c.dependentId,
          name: c.dependentName || "Guest",
          designation: "Visitor",
          accessType: c.isPermanent ? "Permanent" : "Temporary",
          accessCode: c.code,
          expiresAt: c.expiresAt,
          qrValue: c.qrCodeFileUrl || buildAccessLink(c.tenantId, c.id, c.code),
        }));
      } else {
        combined.forEach((t) => {
          if (t._type === "tenant" && t.isLatestAccessCodeActive && t.latestAccessCode) {
            activeCards.push({
              id: t.latestAccessCodeId || `${t.id}_code`,
              tenantId: t.tenantId || t.id,
              dependentId: t.dependentId,
              name: `${t.firstName || ""} ${t.lastName || ""}`.trim(),
              designation: enumOf(t, "dependentType") || "Guest",
              accessType: "Active Pass",
              accessCode: t.latestAccessCode,
              qrValue: buildAccessLink(t.tenantId || t.id, t.id, t.latestAccessCode),
            });
          }
        });
      }

      setTenantState({ accessCards: activeCards });
    } catch (e) {
      console.error("fetchSideData error:", e);
    } finally {
      setIsDataLoading(false);
    }
  }, [estateId, tenantId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    if (fetchedEstateId.current === estateId) return;
    fetchedEstateId.current = estateId;
    setPageReady(false);
    Promise.all([
      fetchSideData(),
      fetchActiveVisits(),
      fetchAccessLogs(logPage, logFilters, true),
    ]).finally(() => {
      setPageReady(true);
    });
  }, [fetchSideData, fetchActiveVisits, fetchAccessLogs, estateId, logPage, logFilters]);

  useEffect(() => {
    if (pageReady) {
      fetchAccessLogs(logPage, logFilters);
    }
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [logPage, logFilters, fetchAccessLogs, pageReady]);

  const handleFilterChange = (key, value) => {
    setLogFilters((prev) => ({ ...prev, [key]: value }));
    setLogPage(1);
  };

  const handleDatePresetChange = (e) => {
    const preset = e.target.value;
    let start = "";
    let end = "";

    if (preset === "all") {
      start = "";
      end = "";
    } else {
      const today = new Date();
      end = today.toISOString().split("T")[0];
      if (preset === "today") {
        start = end;
      } else if (preset === "last7") {
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);
        start = last7.toISOString().split("T")[0];
      } else if (preset === "last30") {
        const last30 = new Date();
        last30.setDate(today.getDate() - 30);
        start = last30.toISOString().split("T")[0];
      }
    }

    setLogFilters((prev) => ({
      ...prev,
      datePreset: preset,
      startDate: start,
      endDate: end,
    }));
    setLogPage(1);
  };

  const handleFullRefresh = useCallback(() => {
    pageCacheRef.current = {};
    fetchSideData();
    fetchActiveVisits();
    fetchAccessLogs(logPage, logFilters, true);
  }, [fetchSideData, fetchActiveVisits, fetchAccessLogs, logPage, logFilters]);

  // Expired codes can complete checkout, so an overstaying dependant can be signed out from here.
  const [checkingOutId, setCheckingOutId] = useState(null);
  const checkOutWait = useRateLimit(`POST /estates/:id/access-logs/check-out`);
  const handleOverstayCheckOut = async (o) => {
    const code = o.code || o.accessCode;
    if (!code || checkOutWait > 0) return;
    if (!(await confirmDialog({ title: "Check Out", message: `Check ${o.name} out now? Their pass expired, but the checkout will still be recorded.`, confirmLabel: "Check Out" }))) return;
    setCheckingOutId(o.id);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs/check-out`, {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const errObj = safeJsonParse(await res.text());
        throw new Error(errObj?.message || errObj?.title || "Failed to check out");
      }
      toast.success(`${o.name} checked out.`);
      handleFullRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCheckingOutId(null);
    }
  };

  const handleViewLog = (logItem) => {
    setModalImgError(false);
    setSelectedItem(logItem);
    setIsModalOpen(true);
    const id = logItem?.baseId || logItem?.id;
    if (id && !passInfo[id]) {
      fetchPassInfo({ apiBase: API_BASE, estateId, logs: [{ ...logItem, id }], authFetch, includeCheckedOut: true })
        .then((found) => { if (Object.keys(found).length) setPassInfo((prev) => ({ ...prev, ...found })); });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  // Prefer the server's summary; the counts worked out from loaded logs cover a tenant's
  // own view and the moment before the summary arrives.
  const monthlyStats = useMemo(() => {
    const today = accessSummary?.today;
    if (today) {
      return {
        entriesToday: today.entries ?? 0,
        visitorsToday: today.visitors ?? 0,
        residentsToday: today.residents ?? 0,
        currentlyInside: today.currentlyInside ?? insidePeople.length,
        overstays: today.currentOverstays ?? overstayers.length,
        fromServer: true,
      };
    }
    return {
      entriesToday: todayLogs.length,
      visitorsToday: todayLogs.filter((l) => isTemporaryPassLog(l, passInfo)).length,
      residentsToday: todayLogs.filter((l) => isResidentLog(l, passInfo)).length,
      currentlyInside: insidePeople.length,
      overstays: overstayers.length,
      fromServer: false,
    };
  }, [accessSummary, todayLogs, insidePeople.length, passInfo, overstayers.length]);


  const logTotalPages = Math.max(1, Math.ceil(logTotalCount / LOG_PAGE_SIZE));

  return (
    <>
      <Dashboard6Styles />
      <style>{`
        @keyframes borderPulseDanger {
          0% { border-color: #ef4444; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4) !important; }
          50% { border-color: #dc2626; box-shadow: 0 0 0 6px rgba(239, 68, 68, 0) !important; }
          100% { border-color: #ef4444; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0) !important; }
        }
        .border-pulse-danger { animation: borderPulseDanger 1.8s infinite ease-in-out !important; border-color: #ef4444 !important; }
      `}</style>

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>

              <PageHeader breadcrumb="Estate Access" title="Estate Access Management">
                <button
                  type="button"
                  onClick={() => {
                    if (window.bootstrap?.Modal && downloadLogModalRef.current) {
                      window.bootstrap.Modal.getOrCreateInstance(downloadLogModalRef.current, { backdrop: "static" }).show();
                    }
                  }}
                  className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                >
                  <i className="ki-outline ki-file-down fs-6 me-sm-2 text-white" />
                  <span className="d-none d-sm-inline">Download Log</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    
                    {/* Top Metric Cards */}
                    <div className="d6-kpi-brand p-6 mb-5">
                      {isDataLoading ? (
                        <div className="row g-0">
                          {[1, 2, 3, 4].map((n) => (
                            <StatCardSkeleton key={n} />
                          ))}
                        </div>
                      ) : (
                        <div className="row g-4 g-xl-0 align-items-center position-relative" style={{ zIndex: 2 }}>
                          {[
                            { icon: "ki-entrance-left", label: "Total Entries Today", value: monthlyStats.entriesToday, sub: "Gate check-ins" },
                            { icon: "ki-user-tick", label: "Residents Today", value: monthlyStats.residentsToday, sub: checkingPasses && !monthlyStats.fromServer ? "Checking…" : "Permanent passes" },
                            { icon: "ki-people", label: "Visitors Today", value: monthlyStats.visitorsToday, sub: checkingPasses && !monthlyStats.fromServer ? "Checking…" : "Temporary passes" },
                            {
                              icon: "ki-home-2",
                              label: "Inside Now",
                              value: monthlyStats.currentlyInside,
                              sub: monthlyStats.overstays > 0 ? `${monthlyStats.overstays} overstayed` : "No overstays",
                            },
                          ].map((item, i) => (
                            <div key={item.label} className="col-12 col-sm-6 col-xl-3">
                              <div className={`px-4 py-2 h-100 ${i < 3 ? "border-end-xl border-white border-opacity-20" : ""}`}>
                                <div className="d-flex align-items-center">
                                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                                      <i className={`ki-outline ${item.icon} fs-2 text-white`} />
                                    </span>
                                  </div>
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="text-white text-opacity-80 fs-8 fw-semibold">{item.label}</div>
                                    <div className="d6-num text-white my-0.5">{item.value}</div>
                                    <div className="text-white text-opacity-70 fs-9">{item.sub}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Overstay alert */}
                    {overstayers.length > 0 && (
                      <div
                        className="d-flex align-items-center gap-4 flex-wrap rounded-4 px-5 py-4 mb-5"
                        style={{ background: "#fef2f2", border: "1px solid #fecaca" }}
                        role="alert"
                      >
                        <span className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: 44, height: 44, background: "#dc2626" }}>
                          <i className="ki-outline ki-time fs-3 text-white" />
                        </span>
                        <div className="min-w-0 flex-grow-1">
                          <div className="fw-bold fs-6" style={{ color: "#b91c1c" }}>
                            {overstayers.length} {overstayers.length === 1 ? "dependant has" : "dependants have"} overstayed a temporary pass
                          </div>
                          <div className="fs-8 text-truncate" style={{ color: "#dc2626" }}>
                            {overstayers.slice(0, 3).map((o) => o.name).join(", ")}
                            {overstayers.length > 3 ? ` and ${overstayers.length - 3} more` : ""} · still inside after their pass expired
                          </div>
                        </div>
                        <button type="button" className="btn btn-danger d6-mbtn flex-shrink-0" onClick={() => setIsOverstayModalOpen(true)}>
                          <i className="ki-outline ki-eye fs-5 me-2" />View &amp; contact hosts
                        </button>
                      </div>
                    )}

                    {/* Access Log Card */}
                    <div className="d6-card p-6 mb-5">
                      <CardHead title="Full Access Log" subtitle="Real-time history of estate entries and exits" className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400" />
                          <input
                            type="text"
                            className="d6m-input ps-10"
                            style={{ width: 240, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Search name, code..."
                            value={logFilters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                          />
                        </div>
                        <select
                          className="d6m-input"
                          style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={logFilters.isActiveVisit}
                          onChange={(e) => handleFilterChange("isActiveVisit", e.target.value)}
                        >
                          <option value="">All visits</option>
                          <option value="true">Active only</option>
                          <option value="false">Checked out</option>
                        </select>
                        <select
                          className="d6m-input"
                          style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={logFilters.verificationMethod}
                          onChange={(e) => handleFilterChange("verificationMethod", e.target.value)}
                        >
                          <option value="">All methods</option>
                          <option value="0">Access Code</option>
                          <option value="1">QR Code</option>
                          <option value="2">Access Card</option>
                          <option value="3">Phone Number</option>
                        </select>
                        <select
                          className="d6m-input"
                          style={{ width: 160, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={logFilters.vehicle}
                          onChange={(e) => handleFilterChange("vehicle", e.target.value)}
                        >
                          <option value="">All transport</option>
                          <option value="with">With vehicle</option>
                          <option value="without">Without vehicle</option>
                        </select>
                        <select
                          className="d6m-input"
                          style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={logFilters.datePreset}
                          onChange={handleDatePresetChange}
                        >
                          <option value="today">Today</option>
                          <option value="last7">Last 7 Days</option>
                          <option value="last30">Last 30 Days</option>
                          <option value="all">All Time</option>
                        </select>
                      </CardHead>

                      <div>
                        {logLoading ? (
                          <ActivityLogSkeleton />
                        ) : activityData.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-shield-tick fs-3x text-gray-300 d-block mb-3" />
                            <div className="d6-title text-gray-900 mb-1">No access logs found.</div>
                            <div className="fs-8 text-gray-500">Visitor activity and gate movements will appear here.</div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex flex-column gap-3">
                              {activityData
                                .filter((event) => {
                                  if (!logFilters.vehicle) return true;
                                  const hasVeh = Boolean(getLogVehicle(event));
                                  return logFilters.vehicle === "with" ? hasVeh : !hasVeh;
                                })
                                .map((event) => {
                                const houseStr = (() => {
                                  const phase = event.phaseName || event.tenant?.currentBuilding?.phaseName || "";
                                  const num = event.buildingNumber || event.houseNumber || event.tenant?.currentBuilding?.number || "";
                                  if (phase && num) return `${phase} - ${num}`;
                                  if (num) return num;
                                  if (phase) return phase;
                                  if (event.tenantBuilding || event.hostBuilding) return event.tenantBuilding || event.hostBuilding;
                                  return "—";
                                })();

                                const isEntry = directionOf(event.direction) === "In";
                                const isOverstay = checkIsOverstay(event, passInfo);
                                const displayName = event.subjectName || event.dependentName || event.visitorName || "Guest";

                                return (
                                  <div
                                    key={event.id}
                                    onClick={() => handleViewLog(event)}
                                    className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all position-relative overflow-hidden ${
                                      isOverstay
                                        ? "border-pulse-danger bg-light-danger bg-opacity-20"
                                        : isEntry
                                        ? "border-hover-success"
                                        : "border-hover-danger"
                                    }`}
                                    style={{ minHeight: 90 }}
                                  >
                                    <div className={`position-absolute top-0 start-0 h-100 ${isOverstay ? "bg-danger w-6px" : isEntry ? "bg-success w-4px" : "bg-danger w-4px"}`} />

                                    <div className="d-flex align-items-center me-4" style={{ width: "420px", flexShrink: 0 }}>
                                      <Avatar
                                        src={event.subjectImageUrl && !imageErrors[event.baseId] ? event.subjectImageUrl : null}
                                        name={displayName}
                                        size={48}
                                      />
                                      <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                        <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                          <span className="fs-6 fw-bold text-gray-900 text-truncate">
                                            {toTitleCase(event.subjectName || event.dependentName || event.visitorName || "Unknown Visitor")}
                                          </span>
                                          {event.subjectType && (
                                            <span
                                              className={`badge fs-9 fw-semibold rounded-pill px-2.5 py-0.5 ${
                                                event.subjectType === "Tenant"
                                                  ? "bg-light-success text-success"
                                                  : event.subjectType === "Dependent"
                                                  ? "bg-light-primary text-primary"
                                                  : "bg-light-info text-info"
                                              }`}
                                            >
                                              {maskType(event.subjectType)}
                                            </span>
                                          )}
                                          {(() => {
                                            const veh = getLogVehicle(event);
                                            const vehName = veh ? [veh.brand, veh.model].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(" ") : "";
                                            return veh && !veh.isUnlisted ? (
                                              <span className="badge bg-light text-gray-800 fs-9 fw-semibold rounded-pill px-2 py-0.5 d-flex align-items-center gap-1">
                                                <i className="ki-outline ki-car fs-8 text-gray-700" />
                                                <span>{vehName || veh.plate || "Vehicle"}</span>
                                              </span>
                                            ) : null;
                                          })()}
                                        </div>
                                        <div className="text-gray-500 fs-8 text-truncate" title={houseStr}>
                                          {houseStr}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                                      <div className="d-none d-sm-block text-center">
                                        <div className="text-gray-400 fs-9 mb-0.5">Method</div>
                                        <div className={`fw-bold fs-8 ${getMethodColorClass(event.verificationMethod)}`}>
                                          {maskMethod(event.verificationMethod)}
                                        </div>
                                      </div>
                                      <div className="d-none d-sm-block text-center">
                                        <div className="text-gray-400 fs-9 mb-0.5">Date</div>
                                        <div className="fw-semibold text-gray-900 fs-8">{fmtDate(event.rawTime)}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-gray-400 fs-9 mb-0.5">{isEntry ? "Clock In" : "Clock Out"}</div>
                                        <div className="fw-bold text-gray-900 fs-8">{event.time}</div>
                                      </div>
                                      <span className="d6-icon-btn" title={isEntry ? "Entry" : "Exit"}>
                                        <i className={`ki-outline ${isEntry ? "ki-entrance-left text-success" : "ki-entrance-right text-danger"} fs-5`} />
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {logTotalPages > 1 && (
                              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-6 pt-5 border-top border-gray-200">
                                <div className="text-gray-500 fs-8">
                                  Showing {(logPage - 1) * LOG_PAGE_SIZE + 1}–{Math.min(logPage * LOG_PAGE_SIZE, logTotalCount)} of{" "}
                                  {logTotalCount} records
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <button type="button" className="d6-icon-btn" disabled={logPage === 1} onClick={() => setLogPage((p) => p - 1)}>
                                    <i className="ki-outline ki-left fs-5" />
                                  </button>
                                  {Array.from({ length: logTotalPages }, (_, i) => i + 1)
                                    .filter((p) => p === 1 || p === logTotalPages || Math.abs(p - logPage) <= 1)
                                    .reduce((acc, p, idx, arr) => {
                                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                      acc.push(p);
                                      return acc;
                                    }, [])
                                    .map((p, idx) =>
                                      p === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">…</span>
                                      ) : (
                                        <button
                                          key={p}
                                          type="button"
                                          className={`btn btn-sm d6-mbtn ${logPage === p ? "btn-success" : "btn-light"}`}
                                          style={{ height: 32, padding: "0 12px" }}
                                          onClick={() => setLogPage(p)}
                                        >
                                          {p}
                                        </button>
                                      )
                                    )}
                                  <button type="button" className="d6-icon-btn" disabled={logPage === logTotalPages} onClick={() => setLogPage((p) => p + 1)}>
                                    <i className="ki-outline ki-right fs-5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Overstayers Modal */}
                    <InlineModal
                      open={isOverstayModalOpen && overstayers.length > 0}
                      icon="ki-time"
                      title={`Overstayed Passes (${overstayers.length})`}
                      onClose={() => setIsOverstayModalOpen(false)}
                      width={760}
                      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={() => setIsOverstayModalOpen(false)}>Close</button>}
                    >
                      <div className="d-flex flex-column gap-4">
                        {overstayers.map((o) => {
                          const nowIso = new Date().toISOString();
                          const expiredIso = o.limitAt ? new Date(o.limitAt).toISOString() : null;
                          const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
                          return (
                            <div key={o.id} className="rounded-4 p-4" style={{ border: "1px solid #fecaca", background: "#fffafa" }}>
                              <div className="d6m-person">
                                <Avatar src={o.subjectImageUrl || null} name={o.name} size={48} />
                                <div className="min-w-0 flex-grow-1">
                                  <div className="d6m-name text-truncate">{o.name}</div>
                                  <div className="d6m-muted text-truncate">{o.subjectPhoneNumber || "No phone number attached"}</div>
                                </div>
                                <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                                  <StatusChip color="#DC2626">{expiredIso ? `Overstayed ${getDuration(expiredIso, nowIso)}` : "Overstayed"}</StatusChip>
                                  {o.dependentType && (
                                    <span className="d6m-muted fs-8">{String(o.dependentType).replace(/([a-z])([A-Z])/g, "$1 $2")} · temporary pass</span>
                                  )}
                                </div>
                              </div>

                              <div className="d6m-kv mt-4" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                                <div className="d6m-kv-cell">
                                  <span className="d6m-k">Checked in</span>
                                  <span className="d6m-v">{o.checkedInAt ? fmt(o.checkedInAt) : "—"}</span>
                                  <div className="d6m-muted mt-1">{officerLabel(o, "in")}</div>
                                </div>
                                <div className="d6m-kv-cell">
                                  <span className="d6m-k">Pass expired</span>
                                  <span className="d6m-v" style={{ color: "#dc2626" }}>{expiredIso ? fmt(expiredIso) : "Expired"}</span>
                                </div>
                                <div className="d6m-kv-cell">
                                  <span className="d6m-k">Access code</span>
                                  <span className="d6m-v">{o.code || o.accessCode || "—"}</span>
                                </div>
                              </div>

                              <div className="d6m-section mt-4 mb-0">
                                <div className="d6m-section-head"><span>Host resident</span></div>
                                <div className="d6m-person flex-wrap">
                                  <Avatar src={o.host.imageUrl} name={o.host.name} size={44} />
                                  <div className="min-w-0 flex-grow-1">
                                    <div className="d6m-name fs-7 text-truncate">{o.host.name}</div>
                                    <div className="d6m-muted text-truncate">
                                      {[maskType(o.host.type), o.host.building !== "—" ? o.host.building : null, o.host.phone].filter(Boolean).join(" · ")}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2 flex-shrink-0">
                                    {o.host.phone ? (
                                      <a href={`tel:${o.host.phone}`} className="btn btn-success d6-mbtn d6-mbtn-sm">
                                        <i className="ki-outline ki-phone fs-6 me-2 text-white" />Call host
                                      </a>
                                    ) : (
                                      <span className="d6m-muted align-self-center">No host phone</span>
                                    )}
                                    {o.host.link && (
                                      <Link to={o.host.link} className="btn btn-light d6-mbtn d6-mbtn-sm">Host details</Link>
                                    )}
                                    <button
                                      type="button"
                                      className="btn btn-light d6-mbtn d6-mbtn-sm"
                                      onClick={() => { setIsOverstayModalOpen(false); handleViewLog({ ...o, baseId: o.id }); }}
                                    >
                                      View pass
                                    </button>
                                    {(o.code || o.accessCode) && (
                                      <button
                                        type="button"
                                        className="btn btn-danger d6-mbtn d6-mbtn-sm"
                                        disabled={checkingOutId === o.id || checkOutWait > 0}
                                        onClick={() => handleOverstayCheckOut(o)}
                                      >
                                        {checkingOutId === o.id ? (
                                          <span className="spinner-border spinner-border-sm" />
                                        ) : checkOutWait > 0 ? `Wait ${checkOutWait}s` : (
                                          <><i className="ki-outline ki-entrance-right fs-6 me-2 text-white" />Check out</>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </InlineModal>

                    {/* View Details Modal */}
                    {(() => {
                      if (!isModalOpen || !selectedItem) return null;
                      const phaseStr = selectedItem.phaseName || selectedItem.tenant?.currentBuilding?.phaseName || "";
                      const numStr =
                        selectedItem.buildingNumber ||
                        selectedItem.houseNumber ||
                        selectedItem.tenant?.currentBuilding?.number ||
                        "";

                      let displayBuilding = "—";
                      if (phaseStr && numStr) displayBuilding = `${phaseStr} - ${numStr}`;
                      else if (numStr) displayBuilding = numStr;
                      else if (phaseStr) displayBuilding = phaseStr;
                      else if (selectedItem.tenantBuilding || selectedItem.hostBuilding) {
                        displayBuilding = selectedItem.tenantBuilding || selectedItem.hostBuilding;
                      }

                      const owner = selectedItem.tenant || {
                        id: selectedItem.tenantId || selectedItem.hostId,
                        fullName:
                          selectedItem.accessCodeCreatedByName ||
                          selectedItem.tenantFullName ||
                          selectedItem.hostName ||
                          "Host Resident",
                        phone: selectedItem.tenantPhone || selectedItem.hostPhoneNumber || selectedItem.hostPhone || null,
                        avatarUrl: selectedItem.tenantImageUrl || selectedItem.hostImageUrl || null,
                        building: displayBuilding,
                      };

                      if (selectedItem.tenant && !owner.building) {
                        owner.building = displayBuilding;
                      }

                      const subjectName = toTitleCase(
                        selectedItem.subjectName || selectedItem.dependentName || selectedItem.visitorName || "Unknown Visitor"
                      );
                      const fmtStamp = (iso) =>
                        new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
                      const isInside = selectedItem.isActiveVisit || (selectedItem.checkedInAt && !selectedItem.checkedOutAt);
                      const selVehicle = getLogVehicle(selectedItem);
                      const arrivedOnFoot = !selVehicle && accessModeLabel(enumOf(selectedItem, "checkInAccessMode")) === "On foot";
                      const passLimitAt = getPassLimitMs(selectedItem, passInfo);
                      const showOverstay = isInside && checkIsOverstay(selectedItem, passInfo);
                      const typeColor = selectedItem.subjectType === "Tenant" ? "#16A34A" : selectedItem.subjectType === "Dependent" ? "#2563EB" : "#0891B2";

                      return (
                        <InlineModal
                          open
                          icon="ki-shield-tick"
                          title="Access Pass Details"
                          onClose={closeModal}
                          width={720}
                          footer={<button type="button" className="btn btn-light d6-mbtn" onClick={closeModal}>Close</button>}
                        >
                          <div className="d6m-person">
                            <Avatar
                              src={selectedItem.subjectImageUrl && !modalImgError ? selectedItem.subjectImageUrl : null}
                              name={subjectName}
                              size={64}
                            />
                            <div className="min-w-0 flex-grow-1">
                              <div className="d6m-name text-truncate">{subjectName}</div>
                              <div className="d6m-muted text-truncate">
                                {selectedItem.subjectPhoneNumber || "No phone number attached"}
                              </div>
                            </div>
                            {selectedItem.subjectType && <StatusChip color={typeColor}>{maskType(selectedItem.subjectType)}</StatusChip>}
                          </div>

                          <div className="d6m-kv d6m-kv-4 mt-5" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                            <div className="d6m-kv-cell">
                              <span className="d6m-k">Method</span>
                              <span className="d6m-v">{maskMethod(selectedItem.verificationMethod)}</span>
                            </div>
                            <div className="d6m-kv-cell" style={{ borderTop: 0 }}>
                              <span className="d6m-k">Building</span>
                              <span className="d6m-v text-truncate d-block">{displayBuilding}</span>
                            </div>
                            <div className="d6m-kv-cell" style={{ borderTop: 0, borderLeft: "1px solid #ececef" }}>
                              <span className="d6m-k">Access Code</span>
                              <span className="d6m-v">{selectedItem.code || selectedItem.accessCode || "—"}</span>
                            </div>
                          </div>

                          {showOverstay && (
                            <div className="d6m-note" style={{ background: "#fef2f2", color: "#b91c1c" }}>
                              <i className="ki-outline ki-information-5" style={{ color: "#dc2626" }} />
                              <span>
                                <strong>Pass time limit exceeded.</strong> This dependant's temporary pass {passLimitAt ? <>expired {fmtStamp(passLimitAt)} ({getDuration(new Date(passLimitAt).toISOString(), new Date().toISOString())} ago)</> : "has expired"} and they are still inside. Please contact the host or notify estate security.
                              </span>
                            </div>
                          )}

                          {selVehicle && (
                            <div className="d6m-section">
                              <div className="d6m-section-head">
                                <span>Arrived by vehicle</span>
                                {selVehicle.plate && <StatusChip color="#16A34A">{selVehicle.plate}</StatusChip>}
                              </div>
                              <div className="d6m-property">
                                {(selVehicle.imageUrl || selVehicle.brand) && !selVehicle.isUnlisted ? (
                                  <img
                                    src={selVehicle.imageUrl || `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(selVehicle.brand)}&modelFamily=${encodeURIComponent(selVehicle.model || "")}&angle=01&background=transparent&format=png`}
                                    alt="Car"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                  />
                                ) : (
                                  <span className="d6m-tile" style={{ width: 64, height: 64 }}><i className="ki-outline ki-car" /></span>
                                )}
                                <div className="min-w-0 flex-grow-1">
                                  {selVehicle.isUnlisted ? (
                                    <>
                                      <div className="d6m-v text-truncate">Unlisted vehicle</div>
                                      <div className="d6m-muted">Not registered to a resident, so no car details were recorded.</div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="d6m-v text-truncate">{[selVehicle.brand, selVehicle.model].filter(Boolean).join(" ") || "Vehicle"}</div>
                                      <div className="d6m-muted">Color: {selVehicle.color || "—"}</div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {arrivedOnFoot && (
                            <div className="d6m-section">
                              <div className="d6m-section-head"><span>Arrived on foot</span></div>
                            </div>
                          )}

                          {selectedItem.subjectType !== "Tenant" && (
                            <div className="d6m-section">
                              <div className="d6m-section-head"><span>Host resident</span></div>
                              <div className="d6m-person">
                                <Avatar
                                  src={selectedItem.accessCodeCreatedByType === "Tenant" ? owner.avatarUrl : null}
                                  name={selectedItem.accessCodeCreatedByName || owner.fullName || "Host"}
                                  size={44}
                                />
                                <div className="min-w-0 flex-grow-1">
                                  <div className="d6m-name fs-7 text-truncate">{toTitleCase(selectedItem.accessCodeCreatedByName || owner.fullName || "—")}</div>
                                  <div className="d6m-muted text-truncate">
                                    {[
                                      maskType(selectedItem.accessCodeCreatedByType || "Tenant"),
                                      owner.building && owner.building !== "—" ? owner.building : null,
                                      owner.phone,
                                    ].filter(Boolean).join(" · ")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="d6m-section">
                            <div className="d6m-section-head"><span>Access log</span></div>
                            <div className="d6m-kv">
                              <div className="d6m-kv-cell">
                                <span className="d6m-k">Checked in</span>
                                <span className="d6m-v">{selectedItem.checkedInAt ? fmtStamp(selectedItem.checkedInAt) : "Pending entry"}</span>
                                {selectedItem.checkedInAt && <div className="d6m-muted mt-1">{officerLabel(selectedItem, "in")}</div>}
                              </div>
                              <div className="d6m-kv-cell">
                                <span className="d6m-k">Checked out</span>
                                <span className="d6m-v">{selectedItem.checkedOutAt ? fmtStamp(selectedItem.checkedOutAt) : "Still inside estate"}</span>
                                {selectedItem.checkedOutAt && <div className="d6m-muted mt-1">{officerLabel(selectedItem, "out")}</div>}
                              </div>
                            </div>
                            <div className="mt-3">
                              <VisitProgressBar
                                checkIn={selectedItem.checkedInAt}
                                checkOut={selectedItem.checkedOutAt}
                                isActive={isInside}
                                subjectType={selectedItem.subjectType}
                                limitAt={passLimitAt}
                              />
                            </div>
                          </div>
                        </InlineModal>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Modals */}
              <GateActionModal
                modalRef={checkInModalRef}
                actionType="in"
                estateId={estateId}
                onSuccess={handleFullRefresh}
                onClose={() => {}}
              />
              <GateActionModal
                modalRef={checkOutModalRef}
                actionType="out"
                estateId={estateId}
                onSuccess={handleFullRefresh}
                onClose={() => {}}
              />
              <DownloadAccessLogModal modalRef={downloadLogModalRef} rawAccessLogs={rawAccessLogs} />

              {/* Footer */}
              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-4">
                  <div className="text-gray-900 text-center fs-7">
                    <span className="text-gray-600 fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-600 text-hover-primary fw-bold">
                      Powered by Pixels Solutions Limited
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}